import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createSignupSchema } from '../schemas/signupSchema';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../api/myAxios';
import {
  Container,
  Box,
  Typography,
  Alert,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SignUpFormData } from '../types';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';

function SignUp() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  usePageTitle('Sign Up');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    getValues,
  } = useForm<SignUpFormData>({
    resolver: yupResolver(createSignupSchema(t)),
    defaultValues: {
      email: '',
      displayName: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);

    try {
      setSuccess('');
      const userCredential = await auth.createUserWithEmailAndPassword(
        data.email,
        data.password
      );
      const user = userCredential.user;

      await user?.updateProfile({
        displayName: data.displayName,
      });

      // await user?.sendEmailVerification();

      await axios.post('/api/users', {
        uid: user?.uid,
        email: user?.email,
        displayName: data.displayName,
      });

      setSuccess(t('register.signUpSuccessful'));
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error(t('error.console.errorSigningUp'), error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('email', {
            type: 'manual',
            message: t('register.emailAlreadyInUse'),
          });
          break;
        case 'auth/weak-password':
          setError('password', {
            type: 'manual',
            message: t('register.weakPassword'),
          });
          break;
        case 'auth/invalid-email':
          setError('email', {
            type: 'manual',
            message: t('register.invalidEmail'),
          });
          break;
        case 'auth/network-request-failed':
          setError('email', {
            type: 'manual',
            message: t('register.networkError'),
          });
          break;
        default:
          setError('email', {
            type: 'manual',
            message: t('register.genericError'),
          });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Typography
          component="h1"
          variant="h4"
          fontWeight="bold"
          gutterBottom
          textAlign="center"
          color="primary"
        >
          {t('register.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 4 }}
        >
          {t('register.description')}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ width: ' 100%', mb: 3 }}>
            {success}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <TextField
              label={t('register.fullName')}
              variant="outlined"
              margin="normal"
              type="text"
              error={!!errors.displayName}
              helperText={errors.displayName?.message}
              {...register('displayName', {
                required: t('register.displayNameRequired'),
              })}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label={t('register.email')}
              variant="outlined"
              margin="normal"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label={t('register.password')}
              variant="outlined"
              margin="normal"
              type={showPassword ? 'text' : 'password'}
              autoComplete="off"
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label={t('register.confirmPassword')}
              variant="outlined"
              margin="normal"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="off"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              {...register('confirmPassword', {
                validate: (value) => {
                  if (value !== getValues('password')) {
                    return t('register.passwordsMustMatch');
                  }
                  return true;
                },
              })}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              color="primary"
              disabled={isLoading || !isValid}
              sx={{ mt: 3, mb: 3 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('register.signUp')
              )}{' '}
            </Button>
          </form>
        </Paper>
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('register.alreadyHaveAccount')}{' '}
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {t('register.signIn')}
              </Typography>
            </Link>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default SignUp;
